import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, X, Expand } from "lucide-react";
import { Reveal, Eyebrow } from "./motion";

const A = "https://customer-assets-39nsmqrw.emergentagent.net/job_scalp-ritual-silk/artifacts";

const STORIES = [
  {
    title: "La storia di F.",
    caption: "Caduta intensa · Kit Boost & Fill — 2 mesi",
    images: [`${A}/6vamhfl4_IMG_0364.jpeg`, `${A}/wmqjw6pm_IMG_0366.jpeg`],
    labels: ["Prima", "Dopo"],
  },
  {
    title: "Una rinascita",
    caption: "Dopo la chemioterapia · un percorso lungo, pieno di coraggio",
    images: [`${A}/ye28tcqu_IMG_0368.jpeg`, `${A}/13urd5vr_IMG_0370.webp`],
    labels: ["Prima", "Dopo"],
  },
];

const COLLAGE = `${A}/hvzovnm2_IMG_0357.jpeg`;

const DM_IMAGES = [
  `${A}/c0mudtbg_IMG_0367.jpeg`,
  `${A}/khuui9jx_IMG_0359.jpeg`,
  `${A}/2o0g21c9_IMG_0371.jpeg`,
  `${A}/83lhay6a_IMG_0358.jpeg`,
  `${A}/ipv765g1_IMG_0361.jpeg`,
];

const GALLERY = [
  ...STORIES.flatMap((s) =>
    s.images.map((img, j) => ({ src: img, title: s.title, label: s.labels[j], caption: s.caption }))
  ),
  { src: COLLAGE, title: "Il percorso in tre fasi", label: "Prima · Mentre · Dopo", caption: "Tempie e attaccatura" },
  ...DM_IMAGES.map((src, i) => ({
    src,
    title: "Le vostre parole",
    label: `Testimonianza ${i + 1}`,
    caption: "Messaggio reale di una cliente",
  })),
];

export const ResultsSection = () => {
  const [lightbox, setLightbox] = useState(null);

  const next = useCallback(
    () => setLightbox((i) => (i === null ? null : (i + 1) % GALLERY.length)),
    []
  );
  const prev = useCallback(
    () => setLightbox((i) => (i === null ? null : (i - 1 + GALLERY.length) % GALLERY.length)),
    []
  );

  return (
    <section data-testid="results-section" className="py-16 sm:py-24 bg-white/60">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Eyebrow className="justify-center">Prima e dopo</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl text-plum tracking-tight">
            Le storie vere di chi ha scelto il percorso
          </h2>
          <p className="mt-4 text-base sm:text-lg text-plum/60 leading-relaxed">
            Foto condivise dalle nostre clienti, con la loro autorizzazione. Toccala per vederla a schermo intero.
          </p>
        </Reveal>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {STORIES.map((story, i) => (
            <Reveal key={story.title} delay={i * 0.1}>
              <div className="bg-mist rounded-[2rem] p-4 sm:p-5 h-full">
                <div className="grid grid-cols-2 gap-3">
                  {story.images.map((img, j) => {
                    const galleryIndex = i * 2 + j;
                    return (
                      <button
                        key={img}
                        data-testid={`result-${i}-${story.labels[j].toLowerCase()}`}
                        onClick={() => setLightbox(galleryIndex)}
                        className="relative rounded-3xl overflow-hidden border-4 border-white shadow-md group cursor-zoom-in"
                        aria-label={`${story.title} — ${story.labels[j]}, apri a schermo intero`}
                      >
                        <img
                          src={img}
                          alt={`${story.title} — ${story.labels[j]}`}
                          loading="lazy"
                          className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        <span className="absolute bottom-3 left-3 bg-plum/85 text-white text-[10px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full backdrop-blur-sm">
                          {story.labels[j]}
                        </span>
                        <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/85 text-plum flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <Expand className="w-4 h-4" />
                        </span>
                      </button>
                    );
                  })}
                </div>
                <div className="px-2 pt-4 pb-1">
                  <p className="font-serif text-lg text-plum">{story.title}</p>
                  <p className="text-xs text-plum/50 mt-1 uppercase tracking-[0.12em] font-semibold">{story.caption}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-6 grid md:grid-cols-[1fr_1.2fr] gap-6 items-center bg-plum rounded-[2rem] p-6 sm:p-8 texture-grain">
            <button
              data-testid="result-collage"
              onClick={() => setLightbox(4)}
              className="rounded-3xl overflow-hidden border-4 border-white/20 shadow-lg group cursor-zoom-in relative"
              aria-label="Il percorso prima, mentre, dopo — apri a schermo intero"
            >
              <img
                src={COLLAGE}
                alt="Il percorso di ricrescita: prima, mentre, dopo"
                loading="lazy"
                className="w-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
              />
              <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/85 text-plum flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Expand className="w-4 h-4" />
              </span>
            </button>
            <div>
              <h3 className="font-serif text-2xl sm:text-3xl text-highlight tracking-tight">
                Il percorso, passo dopo passo
              </h3>
              <p className="mt-4 text-sm sm:text-base text-white/70 leading-relaxed">
                Prima, mentre, dopo: la ricrescita non arriva in un giorno, ma arriva. È la costanza
                del rituale — applicazione, massaggio, pazienza — che accompagna il capello nel suo
                naturale ciclo di crescita.
              </p>
              <Link
                to="/kit"
                data-testid="results-kit-cta"
                className="inline-flex items-center gap-2 mt-6 px-7 py-3.5 rounded-full bg-lilac text-white font-bold text-xs uppercase tracking-[0.14em] hover:bg-lilac-dark active:scale-95 transition-all duration-300"
              >
                Scegli il tuo percorso <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-14">
            <div className="text-center max-w-xl mx-auto">
              <h3 className="font-serif text-2xl sm:text-3xl text-plum tracking-tight">
                Le vostre parole, in diretta dai messaggi
              </h3>
              <p className="mt-3 text-sm sm:text-base text-plum/60">
                Testimonianze reali inviate dalle clienti. Toccale per leggerle a schermo intero.
              </p>
            </div>
            <div className="mt-8 flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory lg:grid lg:grid-cols-5 lg:overflow-visible">
              {DM_IMAGES.map((img, i) => (
                <button
                  key={img}
                  data-testid={`testimonial-dm-${i}`}
                  onClick={() => setLightbox(5 + i)}
                  className="relative shrink-0 w-60 lg:w-auto snap-center rounded-3xl overflow-hidden border-4 border-white shadow-md group cursor-zoom-in bg-white"
                  aria-label={`Testimonianza ${i + 1}, apri a schermo intero`}
                >
                  <img
                    src={img}
                    alt={`Testimonianza cliente ${i + 1}`}
                    loading="lazy"
                    className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <span className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/85 text-plum flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Expand className="w-4 h-4" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <p className="mt-8 text-xs text-plum/40 text-center max-w-2xl mx-auto leading-relaxed">
            Esperienze individuali condivise dalle nostre clienti. Ogni percorso è unico e i risultati
            possono variare da persona a persona: la cura dei capelli richiede costanza.
          </p>
        </Reveal>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <motion.div
            data-testid="results-lightbox"
            className="fixed inset-0 z-[90] bg-plum/95 backdrop-blur-md flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            <div className="flex items-center justify-between p-5">
              <p className="text-white/60 text-xs uppercase tracking-[0.2em] font-semibold">
                {lightbox + 1} / {GALLERY.length} · {GALLERY[lightbox].label}
              </p>
              <button
                data-testid="lightbox-close-btn"
                onClick={() => setLightbox(null)}
                className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                aria-label="Chiudi galleria"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 flex items-center justify-center px-4 pb-4 min-h-0" onClick={(e) => e.stopPropagation()}>
              <button
                data-testid="lightbox-prev-btn"
                onClick={prev}
                className="hidden sm:flex w-12 h-12 shrink-0 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition-colors mr-4"
                aria-label="Foto precedente"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <motion.figure
                key={lightbox}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="max-h-full flex flex-col items-center min-h-0"
              >
                <img
                  src={GALLERY[lightbox].src}
                  alt={`${GALLERY[lightbox].title} — ${GALLERY[lightbox].label}`}
                  className="max-h-[68vh] w-auto max-w-full rounded-3xl border-4 border-white/15 shadow-2xl object-contain"
                  data-testid="lightbox-image"
                />
                <figcaption className="mt-4 text-center">
                  <p className="font-serif text-lg text-white">{GALLERY[lightbox].title}</p>
                  <p className="text-xs text-white/50 uppercase tracking-[0.14em] font-semibold mt-1">
                    {GALLERY[lightbox].caption}
                  </p>
                </figcaption>
              </motion.figure>
              <button
                data-testid="lightbox-next-btn"
                onClick={next}
                className="hidden sm:flex w-12 h-12 shrink-0 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition-colors ml-4"
                aria-label="Foto successiva"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            <div className="sm:hidden flex justify-center gap-4 pb-8" onClick={(e) => e.stopPropagation()}>
              <button
                data-testid="lightbox-prev-btn-mobile"
                onClick={prev}
                className="w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center"
                aria-label="Foto precedente"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <button
                data-testid="lightbox-next-btn-mobile"
                onClick={next}
                className="w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center"
                aria-label="Foto successiva"
              >
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
